"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The homepage hero's rotation clock.
 *
 * ## Why the rules are shaped this way
 *
 * An auto-advancing hero is a carousel, and a carousel is the single most
 * reliably user-hostile pattern on a marketing page. It is only defensible if
 * it gets out of the way the moment the visitor shows any intent, so the
 * behaviour here is deliberately timid:
 *
 * - **Engagement stops it for the session.** Pressing play, following a CTA,
 *   driving the prev/next controls, or simply putting keyboard focus inside
 *   the hero latches {@link HeroRotation.engaged} and the timer never re-arms
 *   on its own. Nielsen Norman's finding is that the failure mode is the page
 *   moving under a reader, not the reader missing a slide.
 * - **Transient holds pause it.** `held` covers the pointer sitting over the
 *   hero, the tab being hidden, and the hero's own audio playing. Those are
 *   reversible: when the reason goes away the dwell starts over from full.
 * - **Reduced motion disables it entirely.** WCAG's motion preference is not a
 *   request for a shorter animation, and an unannounced content swap is
 *   motion whether or not it is tweened. Under `prefers-reduced-motion` the
 *   hero stays on slide one and only the manual controls work.
 *
 * ## Where SC 2.2.2 is satisfied
 *
 * There is no separate pause button. The hero's prev/next controls are the
 * "Pause, Stop, Hide" mechanism: they are visible, keyboard reachable, and
 * activating either one latches {@link HeroRotation.engaged}, which stops the
 * auto-advance for the rest of the session. {@link HeroRotation.next} and
 * {@link HeroRotation.prev} therefore MUST keep engaging — a version of them
 * that only moved the slide would take the page out of conformance.
 *
 * The timer is a plain `setTimeout` re-armed on every index change rather than
 * an interval, so a hold that arrives mid-dwell can never leave a stale tick
 * queued behind it.
 */

/** Owner-set dwell. Reversible; it is a number, not a rule. */
export const HERO_ROTATION_DWELL_MS = 9000;

export interface UseHeroRotationOptions {
  /** How many slides there are. Rotation is inert below two. */
  count: number;
  /** Milliseconds a slide holds before the next one. */
  dwellMs?: number;
  /** `prefers-reduced-motion: reduce`. Disables auto-advance outright. */
  reducedMotion: boolean;
  /**
   * Reversible reasons to hold the clock: pointer over the hero, tab hidden,
   * hero audio playing. Unlike engagement these do not persist.
   */
  held?: boolean;
}

export interface HeroRotation {
  /** The slide the hero should be showing. */
  index: number;
  /** Latched once the visitor took over. Auto-advance is done for the session. */
  engaged: boolean;
  /** Whether the clock is armed *right now*, transient holds included. */
  autoAdvancing: boolean;
  /** False when rotation can never run here (one slide, or reduced motion). */
  canAutoAdvance: boolean;
  /** Advance one slide, user-driven. Latches engagement and announces. */
  next: () => void;
  /** Go back one slide, user-driven. Latches engagement and announces. */
  prev: () => void;
  /** Latch engagement without moving. For play, CTA clicks and focus. */
  engage: () => void;
  /**
   * The index of the last **user-driven** change, or null if every change so
   * far was the clock's. Auto-advance must never reach a live region, so this
   * is what a polite status node is allowed to read from.
   */
  announceIndex: number | null;
}

export function useHeroRotation({
  count,
  dwellMs = HERO_ROTATION_DWELL_MS,
  reducedMotion,
  held = false,
}: UseHeroRotationOptions): HeroRotation {
  const [rawIndex, setIndex] = useState(0);
  const [engaged, setEngaged] = useState(false);
  const [rawAnnounceIndex, setAnnounceIndex] = useState<number | null>(null);

  // Clamped during render rather than corrected in an effect: a rotation set
  // that shrinks under the hero (a refetch dropping a sound) must never leave
  // a frame pointing at a slide that is not there, and an effect fires one
  // render too late to prevent that.
  const index = count > 0 ? rawIndex % count : 0;
  const announceIndex =
    rawAnnounceIndex === null || count < 1 ? null : rawAnnounceIndex % count;

  const canAutoAdvance = count > 1 && !reducedMotion;
  const autoAdvancing = canAutoAdvance && !engaged && !held;

  useEffect(() => {
    if (!autoAdvancing) return;
    const id = setTimeout(() => {
      // Deliberately does NOT touch announceIndex: an auto-advance that
      // announces itself turns a live region into a metronome.
      setIndex(current => (current + 1) % count);
    }, dwellMs);
    return () => clearTimeout(id);
    // `index` is a dependency so each slide gets a full dwell rather than
    // inheriting the remainder of the previous one.
  }, [autoAdvancing, index, count, dwellMs]);

  // Latest committed index, mirrored so `step` can compute the next one
  // without reaching for a `setIndex` updater. React runs updaters during the
  // render phase and is free to run them more than once, so calling
  // `setAnnounceIndex` from inside one landed a second state update mid-render
  // — the shape TES-1873 removed from /sounds, and the only path on which
  // React switches to its re-render dispatcher (the sole thrower of "Rendered
  // more hooks than during the previous render", Sentry TESLA-LOCK-SOUND-5A).
  // Written on every commit and again in `step`, so two clicks inside one
  // batch still advance two slides.
  const indexRef = useRef(rawIndex);
  useEffect(() => {
    indexRef.current = rawIndex;
  }, [rawIndex]);

  const step = useCallback(
    (delta: number) => {
      if (count < 1) return;
      const nextIndex = (indexRef.current + delta + count) % count;
      indexRef.current = nextIndex;
      setEngaged(true);
      setIndex(nextIndex);
      setAnnounceIndex(nextIndex);
    },
    [count]
  );

  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);

  const engage = useCallback(() => setEngaged(true), []);

  return {
    index,
    engaged,
    autoAdvancing,
    canAutoAdvance,
    next,
    prev,
    engage,
    announceIndex,
  };
}
